import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { KeyIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import publicClient from '../../api/publicClient';
import { useAuth } from '../../context/AuthContext';

export default function SetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    publicClient.get(`/api/set-password/${token}`)
      .then(({ data }) => { if (!cancelled) setCtx(data); })
      .catch(err => {
        if (cancelled) return;
        setExpired(!!err.response?.data?.expired);
        setError(err.response?.data?.error || 'This link is invalid or has expired.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error('Password must be at least 8 characters.');
    if (pw !== pw2)    return toast.error('Passwords don\'t match.');
    setSubmitting(true);
    try {
      const { data } = await publicClient.post(`/api/set-password/${token}`, { password: pw });
      // Hand the session JWT to AuthContext so we land logged in.
      setSession(data.token, data.user);
      toast.success('Password set! Welcome to BBC.');
      // Admins/directors go straight to the admin dashboard (with sidebar).
      // New hires (teacher/float/cook setting up via the candidate flow)
      // land on /my so they continue the onboarding checklist.
      const isManagement = ['director', 'admin'].includes(data.user?.role);
      navigate(isManagement ? '/dashboard' : '/my', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not set password.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-900 flex items-center justify-center"><div className="text-gray-400">Loading…</div></div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className={`${expired ? 'bg-amber-500/10' : 'bg-red-500/10'} rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6`}>
            <ExclamationTriangleIcon className={`w-12 h-12 ${expired ? 'text-amber-400' : 'text-red-400'}`} />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {expired ? 'Link expired' : 'Link unavailable'}
          </h2>
          <p className="text-gray-400">{error}</p>
          <p className="text-gray-500 text-sm mt-4">
            Reach out to BBC and we'll send a fresh link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
      <Toaster position="top-right" />
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-xl">BBC</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Welcome, {ctx?.first_name}!</h1>
          <p className="text-gray-400 mt-1">{ctx?.role_label} — set your password to get started.</p>
        </div>

        <form onSubmit={submit} className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center">
              <KeyIcon className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Choose a password</h2>
              <p className="text-xs text-gray-400">At least 8 characters.</p>
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">New password</label>
            <input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              minLength={8}
              required
              autoComplete="new-password"
              className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Confirm password</label>
            <input
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              minLength={8}
              required
              autoComplete="new-password"
              className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-orange-500"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white font-semibold py-3 px-6 rounded-xl flex items-center justify-center gap-2"
          >
            <CheckCircleIcon className="w-5 h-5" />
            {submitting ? 'Setting…' : 'Set password & sign in'}
          </button>
        </form>

        <p className="text-center text-gray-600 text-sm mt-6">
          Bright Beginnings Childcare — Confidential
        </p>
      </div>
    </div>
  );
}
